import { useMemo } from "react";
import { parseISO } from "date-fns";
import { DEFAULT_FORM_1099_REVIEW_FILTERS } from "@common/helpers/form1099-review-filters";
import { NamedView } from "@common/types/views/views";
import { CounterpartyDirectoryView } from "@common/types/views/counterpartyDirectoryView";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { useWorkspaceAccounts } from "@api/account.api";
import { useEntities } from "@hooks/useEntities";
import { useCounterpartyDirectoryViewConfig } from "./useCounterpartyDirectoryViewConfig";

const PROFIT_AND_LOSS_CLASSES: AccountClass[] = [
    AccountClass.income,
    AccountClass.expenses,
];

interface Review1099ViewDef {
    id: string;
    name: string;
    year: number;
    minAmount: number;
}

const REVIEW_1099_VIEW_DEFS: Review1099ViewDef[] = [
    {
        id: "1099-review-2026",
        name: "1099 review 2026",
        year: 2026,
        minAmount: 2000,
    },
    {
        id: "1099-review-2025",
        name: "1099 review 2025",
        year: 2025,
        minAmount: 600,
    },
];

/**
 * Returns the calendar year a built-in "1099 review" view is scoped to, or
 * undefined when the view is not one of the 1099 review views.
 */
export function getCounterparty1099ReviewYear(
    viewId: string | undefined,
): number | undefined {
    return REVIEW_1099_VIEW_DEFS.find((def) => def.id === viewId)?.year;
}

export function useBuiltInCounterpartyDirectoryViews(): Array<
    NamedView<CounterpartyDirectoryView>
> {
    const { defaultView } = useCounterpartyDirectoryViewConfig();
    const { data: accounts } = useWorkspaceAccounts();
    const entities = useEntities();

    // The 2025 review view only makes sense when there is bookkeeping
    // history before 2026; newer clients only get the current-year view.
    const hasPre2026BookkeepingStart = useMemo(
        () =>
            entities.some(
                (entity) =>
                    parseISO(entity.bookkeepingStartDate).getUTCFullYear() <
                    2026,
            ),
        [entities],
    );

    return useMemo(() => {
        const allCounterpartiesView: NamedView<CounterpartyDirectoryView> = {
            ...defaultView,
            id: "default",
            name: "All counterparties",
            filters: {},
        };

        // 1099 review views scope to the Expenses ledger group via
        // accountClasses rather than resolving individual account ids
        // (coaAccounts) from the workspace chart of accounts — accountClasses
        // is workspace-agnostic (it's the top tier of the ledger group filter,
        // not a per-account lookup), so unlike the previous coaAccounts-based
        // filter, this view no longer needs to wait for the accounts query.
        const review1099Views: Array<NamedView<CounterpartyDirectoryView>> =
            REVIEW_1099_VIEW_DEFS.filter(
                (def) => def.year >= 2026 || hasPre2026BookkeepingStart,
            ).map((def) => ({
                ...defaultView,
                id: def.id,
                name: def.name,
                filters: {
                    ...DEFAULT_FORM_1099_REVIEW_FILTERS,
                    minAmount: def.minAmount,
                },
            }));

        // The Profit & Loss view scopes the directory to income/expense
        // accounts derived from the workspace chart of accounts. Until the
        // accounts query resolves we cannot compute that filter, and exposing
        // the view early would let it be selected (and persisted to the
        // workspace-user cache) with an empty, non-self-correcting filter.
        if (!accounts) {
            return [allCounterpartiesView, ...review1099Views];
        }

        const profitAndLossAccountIds = accounts
            .filter((account) =>
                PROFIT_AND_LOSS_CLASSES.includes(account.class),
            )
            .map((account) => account.id);

        const profitAndLossView: NamedView<CounterpartyDirectoryView> = {
            ...defaultView,
            id: "profit-loss",
            name: "Profit & Loss",
            filters: { coaAccounts: profitAndLossAccountIds },
        };

        return [allCounterpartiesView, profitAndLossView, ...review1099Views];
    }, [defaultView, accounts, hasPre2026BookkeepingStart]);
}
